import EmojiPicker from 'emoji-picker-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Overlay, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import Select from 'react-dropdown-select';
import addMediaIcon from '../../assets/add-media-icon.svg';
import boldIcon from '../../assets/editor-icons/bold-icon.svg';
import codeIcon from '../../assets/editor-icons/code-icon.svg';
import infoIcon from '../../assets/editor-icons/info-icon.svg';
import italicIcon from '../../assets/editor-icons/italic-icon.svg';
import plusIcon from '../../assets/editor-icons/plus-icon.svg';
import smileIcon from '../../assets/editor-icons/smile-icon.svg';
import strikethroughIcon from '../../assets/editor-icons/strikethrough-icon.svg';
import AuthenticationContent from './AuthenticationContent';
import ButtonTypeControl from './ButtonTypeControl';
import MessageValidityPeriod from './MessageValidityPeriod';

const TemplateForm = ({
  isEditing,
  template,
  templateHeader,
  templateBody,
  templateFooter,
  templateButtons,
  bodyVariables,
  bodyVariableEntries,
  codeExp,
  bodyTextareaRef,
  languages,
  selectedLanguage,
  onLanguageChange,
  onTemplateChange,
  onBodyChange,
  setTemplateHeader,
  setTemplateFooter,
  setCodeExp,
  onAddVariable,
  onRemoveVariable,
  onVariableEntryChange,
  onButtonChange,
  onAddButton,
  onRemoveButton,
  handleMediaUpload,
  isUploading,
  uploadError,
  validityPeriod,
  setValidityPeriod,
  securityRecommendation,
  setSecurityRecommendation,
  showExpiryTime,
  setShowExpiryTime,
}) => {
  const allButtonTypes = [
    { value: 'QUICK_REPLY', label: 'Quick Reply' },
    { value: 'URL', label: 'Visit Website' },
    { value: 'PHONE_NUMBER', label: 'Call Phone Number' },
    { value: 'OTP', label: 'One-Time Password' },
  ];

  const buttonTypes =
    template.category === 'AUTHENTICATION'
      ? allButtonTypes.filter((bt) => bt.value === 'OTP')
      : allButtonTypes.filter((bt) => bt.value !== 'OTP');

  const categories = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'UTILITY', label: 'Utility' },
    { value: 'AUTHENTICATION', label: 'Authentication' },
  ];

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddButtonWithType = (type) => {
    setShowDropdown(false);

    const existingButtons = [...templateButtons];

    const urlButtonCount = existingButtons.filter(b => b.type === 'URL').length;
    const phoneButtonCount = existingButtons.filter(b => b.type === 'PHONE_NUMBER').length;
    const copyOfferButtonCount = existingButtons.filter(b =>
      b.type === 'QUICK_REPLY' &&
      b.text &&
      b.text.toLowerCase().includes('copy') &&
      b.text.toLowerCase().includes('offer')
    ).length;
    const optOutButtonCount = existingButtons.filter(b =>
      b.type === 'QUICK_REPLY' &&
      b.text &&
      b.text.toLowerCase().includes('stop')
    ).length;

    if (type === 'URL' && urlButtonCount >= 2) {
      alert('You can only add up to 2 URL buttons per template.');
      return;
    }

    if (type === 'PHONE_NUMBER' && phoneButtonCount >= 1) {
      alert('You can only add 1 Phone Number button per template.');
      return;
    }

    if (type === 'COPY_OFFER_CODE' && copyOfferButtonCount >= 1) {
      alert('You can only add 1 Copy Offer Code button per template.');
      return;
    }

    if (type === 'MARKETING_OPT_OUT' && optOutButtonCount >= 1) {
      alert('You can only add 1 Marketing Opt-Out button per template.');
      return;
    }

    if (type === 'COPY_OFFER_CODE') {
      const newButtonIndex = templateButtons.length;
      onButtonChange(newButtonIndex, {
        type: 'QUICK_REPLY',
        text: 'Copy offer code',
        offerCode: ''
      });
    }
    else if (type === 'MARKETING_OPT_OUT') {
      const newButtonIndex = templateButtons.length;
      onButtonChange(newButtonIndex, {
        type: 'QUICK_REPLY',
        text: 'Stop promotions',
        footerText: 'Not interested? Tap Stop promotions',
        optOutAcknowledged: false
      });
    }
    else {
      onAddButton(type);
    }
  };

  const getCategoryDetailedDescription = (category) => {
    switch (category) {
      case 'MARKETING':
        return 'Marketing is used for promotion or information about your business, products or services, or any message that isn\'t for utility or authentication purpose. E.g. welcome messages, newsletter, offers, coupons, catalogues, new shop opening hours';
      case 'UTILITY':
        return 'Utility is used for transaction, account, order or customer request. E.g. order confirmations, account updates, receipts, appointment reminders, billing (choose marketing if you want to include promotion in utility message)';
      case 'AUTHENTICATION':
        return 'Authentication is used for one-off passwords that your customers use to authenticate a transaction or login. E.g. one-off password, account recovery code.';
      default:
        return '';
    }
  };

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef(null);

  const handleEmojiClick = (emojiData) => {
    const textarea = bodyTextareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateBody;
    const newText = text.substring(0, start) + emojiData.emoji + text.substring(end);
    onBodyChange(newText);
    setShowEmojiPicker(false);
  };

  const handleTextFormat = (formatType) => {
    const textarea = bodyTextareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateBody;
    const selectedText = text.substring(start, end);

    let formatMarkers;
    switch (formatType) {
      case 'bold':
        formatMarkers = ['*', '*'];
        break;
      case 'italic':
        formatMarkers = ['_', '_'];
        break;
      case 'strikethrough':
        formatMarkers = ['~', '~'];
        break;
      case 'monospace':
        formatMarkers = ['```', '```'];
        break;
      default:
        return;
    }

    let newText;
    let newCursorPos;

    if (start === end) {
      newText =
        text.substring(0, start) + formatMarkers[0] + formatMarkers[1] + text.substring(end);
      newCursorPos = start + formatMarkers[0].length;
    } else {
      newText =
        text.substring(0, start) +
        formatMarkers[0] +
        selectedText +
        formatMarkers[1] +
        text.substring(end);
      newCursorPos = end + formatMarkers[0].length + formatMarkers[1].length;
    }

    onBodyChange(newText);

    setTimeout(() => {
      textarea.focus();
      if (start === end) {
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      } else {
        const selectionStart = start + formatMarkers[0].length;
        const selectionEnd = end + formatMarkers[0].length;
        textarea.setSelectionRange(selectionStart, selectionEnd);
      }
    }, 0);
  };

  const handleFileInputChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    await handleMediaUpload(file);
  };

  return (
    <div className="wa-template-form">
      <div className="section-header">
        {!isEditing ? (
          <>
            <h2 className="template-title">Create New Template</h2>
            <div className="subtitle mb-3">Category</div>
            <div className="category-selection mb-4">
              {categories.map((cat) => (
                <div
                  key={cat.value}
                  className="category-option"
                  onClick={() => {
                    if (template.category !== cat.value) {
                      onTemplateChange('category', cat.value);
                      if (cat.value === 'AUTHENTICATION' || template.category === 'AUTHENTICATION') {
                        onRemoveButton(0);
                      }
                    } else {
                      onTemplateChange('category', cat.value);
                    }
                  }}
                >
                  <div
                    className={`radio-circle ${template.category === cat.value ? 'active' : ''}`}
                  />
                  <span>{cat.label}</span>
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id={`category-tooltip-${cat.value}`} className="category-tooltip">
                        {getCategoryDetailedDescription(cat.value)}
                      </Tooltip>
                    }
                  >
                    <div className="info-icon-wrapper">
                      <i className="fas fa-info-circle info-icon" />
                    </div>
                  </OverlayTrigger>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="template-header">
            <div className="icon-title">
              <div className="template-icon">
                <i className="fas fa-pencil-alt"></i>
              </div>
              <div>
                <h2 className="title">Template Name</h2>
                <div className="subtitle">{template.category}</div>
              </div>
            </div>
          </div>
        )}
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Template Name</Form.Label>
              <Form.Control
                type="text"
                value={template.name}
                onChange={(e) => {
                  let value = e.target.value;

                  value = value.toLowerCase();
                  value = value.replace(/\s/g, '_');
                  value = value.replace(/[^a-z0-9_]/g, '');

                  onTemplateChange('name', value);
                }}
                placeholder="Enter Template Name (lowercase, underscores only)"
                disabled={isEditing}
                maxLength={512}
              />
              <div className="char-count">{template.name.length}/512 characters</div>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Language</Form.Label>
              <Select
                values={selectedLanguage}
                onChange={onLanguageChange}
                options={languages}
                placeholder="Select language"
                className="template-language-select"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Template Content */}
      <div className="section-header template-content-header">

        {/* Header */}
        {template.category !== 'AUTHENTICATION' && (
          <>
            <h3 className="template-title">Template Content</h3>
            <div className="subtitle">Fill out the provided form fields for your template.</div>
            <Form.Group className="pt-2">
              <Form.Label>
                <span>
                  Header <span className="optional">• Optional</span>
                </span>
              </Form.Label>

              {/* Header Type Selection */}
              <div className="header-type-selection">
                <div
                  className={`header-type-option ${!template.headerType || template.headerType === 'TEXT' ? 'active' : ''}`}
                  onClick={() => {
                    onTemplateChange('headerType', 'TEXT');
                    if (template.headerType !== 'TEXT') {
                      onTemplateChange('headerMediaHandle', null);
                      onTemplateChange('headerMediaPreviewUrl', null);
                    }
                  }}
                >
                  <div
                    className={`radio-circle ${!template.headerType || template.headerType === 'TEXT' ? 'active' : ''}`}
                  />
                  <span>Text</span>
                </div>
                <div
                  className={`header-type-option ${template.headerType === 'IMAGE' ? 'active' : ''}`}
                  onClick={() => {
                    onTemplateChange('headerType', 'IMAGE');
                    setTemplateHeader('');
                  }}
                >
                  <div
                    className={`radio-circle ${template.headerType === 'IMAGE' ? 'active' : ''}`}
                  />
                  <span>Image</span>
                </div>
                {/* <div
                className={`header-type-option ${template.headerType === 'VIDEO' ? 'active' : ''}`}
                onClick={() => {
                  onTemplateChange('headerType', 'VIDEO');
                  setTemplateHeader('');
                }}
              >
                <div
                  className={`radio-circle ${template.headerType === 'VIDEO' ? 'active' : ''}`}
                />
                <span>Video</span>
              </div> */}
              </div>

              {/* Text Header */}
              {(!template.headerType || template.headerType === 'TEXT') && (
                <>
                  <Form.Control
                    type="text"
                    value={templateHeader}
                    onChange={(e) => setTemplateHeader(e.target.value)}
                    placeholder="Enter header text"
                    maxLength={60}
                  />
                  <div className="char-count">{templateHeader.length}/60 characters</div>
                </>
              )}

              {/* Media Header (Image or Video) */}
              {(template.headerType === 'IMAGE' || template.headerType === 'VIDEO') && (
                <div className="media-upload-container">
                  {template.headerMediaHandle ? (
                    <div className="media-preview">
                      {template.headerType === 'IMAGE' ? (
                        <img
                          src={template.headerMediaPreviewUrl}
                          alt="Header media"
                          className="media-preview-image"
                        />
                      ) : (
                        <video
                          src={template.headerMediaPreviewUrl}
                          controls
                          className="media-preview-video"
                        />
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          onTemplateChange('headerMediaHandle', null);
                          onTemplateChange('headerMediaPreviewUrl', null);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="media-upload">
                      <div className="hint-text">
                        Samples for header content<br />
                        Please provide examples of variables or media used in the header. Do not include any customer information. The Cloud API hosted by Meta will review templates and variable parameters to ensure the security and integrity of the services.
                      </div>
                      <div className="upload-button-container">
                        <div className="file-info">{template.headerType}</div>
                        <label className="upload-button" htmlFor="header-media-upload">
                          <img src={addMediaIcon} alt="Upload" className="upload-icon" />
                          {template.headerType === 'IMAGE' ? 'Choose JPG or PNG file' : 'Choose MP4 file'}
                        </label>
                        <input
                          id="header-media-upload"
                          type="file"
                          className="form-control"
                          accept={template.headerType === 'IMAGE' ? 'image/*' : 'video/*'}
                          onChange={handleFileInputChange}
                        />
                      </div>
                      {/* <div className="text-muted small mt-1">
                      {template.headerType === 'IMAGE'
                        ? 'Recommended size: 800x418px (1.91:1 ratio), max 5MB'
                        : 'Max duration: 30 seconds, max 16MB'}
                    </div> */}
                      {isUploading && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Uploading...
                        </div>
                      )}
                      {uploadError && (
                        <div className="text-danger mt-2">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {uploadError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Form.Group>

          </>
        )}

        {/* Body */}
        <Form.Group>
          {template.category === 'AUTHENTICATION' ? (
            <AuthenticationContent
              bodyVariables={bodyVariables}
              bodyVariableEntries={bodyVariableEntries}
              codeExp={codeExp}
              setCodeExp={setCodeExp}
              onSecurityRecommendationChange={(checked) => {
                setSecurityRecommendation(checked);
              }}
              onExpiryTimeChange={(checked) => {
                setShowExpiryTime(checked);
              }}
              securityRecommendation={securityRecommendation}
              showExpiryTime={showExpiryTime}
              onAddVariable={onAddVariable}
              onRemoveVariable={onRemoveVariable}
              onVariableEntryChange={onVariableEntryChange}
            />
          ) : (
            <>
              <Form.Label>
                <span>Body</span>
              </Form.Label>
              <Form.Control
                ref={bodyTextareaRef}
                as="textarea"
                rows={4}
                value={templateBody}
                onChange={(e) => onBodyChange(e.target.value)}
                placeholder="Type your message here"
                maxLength={1024}
              />
              <div className="editor-toolbar">
                <Button
                  ref={emojiButtonRef}
                  variant="light"
                  className="btn-icon"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                  <img src={smileIcon} alt="Smile" className="emoji-icon" />
                </Button>
                <Overlay
                  target={emojiButtonRef.current}
                  show={showEmojiPicker}
                  placement="top"
                  rootClose
                  onHide={() => setShowEmojiPicker(false)}
                >
                  {({ ...props }) => (
                    <div
                      {...props}
                      style={{
                        ...props.style,
                        padding: '5px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                      }}
                    >
                      <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} />
                    </div>
                  )}
                </Overlay>
                <Button
                  variant="light"
                  className="btn-icon"
                  onClick={() => handleTextFormat('bold')}
                  title="Make text bold (*text*)"
                >
                  <img src={boldIcon} alt="Bold" className="icon" />
                </Button>
                <Button
                  variant="light"
                  className="btn-icon"
                  onClick={() => handleTextFormat('italic')}
                  title="Make text italic (_text_)"
                >
                  <img src={italicIcon} alt="Italic" className="icon" />
                </Button>
                <Button
                  variant="light"
                  className="btn-icon"
                  onClick={() => handleTextFormat('strikethrough')}
                  title="Make text strikethrough (~text~)"
                >
                  <img src={strikethroughIcon} alt="Strikethrough" className="icon" />
                </Button>
                <Button
                  variant="light"
                  className="btn-icon"
                  onClick={() => handleTextFormat('monospace')}
                  title="Format as code (```text```)"
                >
                  <img src={codeIcon} alt="Code" className="icon" />
                </Button>
                {bodyVariables.length < 3 && (
                  <Button variant="light" onClick={onAddVariable} className="add-variable-btn">
                    <img src={plusIcon} alt="Plus" className="plus-icon" />
                    <span>Add Variable</span>
                  </Button>
                )}
                <div className="char-count">{templateBody.length}/1024 characters</div>
              </div>
            </>
          )}
        </Form.Group>

        {/* Variables */}
        {template.category !== 'AUTHENTICATION' && bodyVariables.length > 0 && (
          <div className="variables-section">
            <h6>Variables</h6>
            {bodyVariables.map((variable, index) => (
              <div key={index} className="variable-row">
                <span className="variable-label">{variable}</span>
                <Form.Control
                  type="text"
                  value={bodyVariableEntries[index] || ''}
                  onChange={(e) => onVariableEntryChange(index, e.target.value)}
                  placeholder="Enter example value (required)"
                  className={!bodyVariableEntries[index] ? 'border-warning' : ''}
                />
                <Button variant="outline-danger" onClick={() => onRemoveVariable(index)}>
                  <i className="fas fa-trash"></i>
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {template.category !== 'AUTHENTICATION' && (
          <Form.Group>
            <Form.Label>
              <span>
                Footer <span className="optional">• Optional</span>
              </span>
            </Form.Label>
            <Form.Control
              type="text"
              value={templateFooter}
              onChange={(e) => setTemplateFooter(e.target.value)}
              placeholder="Enter footer text"
              maxLength={60}
            />
            <div className="char-count">{templateFooter.length}/60 characters</div>
          </Form.Group>
        )}

        {/* Buttons */}
        <div className="buttons-section">
          <div className="d-flex align-items-center mb-2 gap-2">
            {template.category !== 'AUTHENTICATION' && (<h6 className="button-title">Button</h6>)}
            {template.category !== 'AUTHENTICATION' && templateButtons.length < 3 && (
              <>
                <div className="dropup" ref={dropdownRef}>
                  <Button
                    variant="outline-primary"
                    className={`add-button-btn dropdown-toggle ${showDropdown ? 'show' : ''}`}
                    id="dropdown-button-add"
                    onClick={toggleDropdown}
                    aria-expanded={showDropdown}
                    disabled={templateButtons.length >= 3}
                    style={{
                      color: '#0BA9E0',
                      borderColor: '#0BA9E0',
                      backgroundColor: 'white',
                      fontSize: '12px',
                      padding: '4px 12px',
                      height: 'auto'
                    }}
                  >
                    Add Button{' '}
                    {templateButtons.length > 0 ? `(${3 - templateButtons.length} remaining)` : ''}
                  </Button>
                  <ul className={`dropdown-menu dropdown-menu-up ${showDropdown ? 'show' : ''}`} aria-labelledby="dropdown-button-add">
                    {/* Marketing Opt-Out option - only shown for Marketing category */}
                    {template.category === 'MARKETING' && (
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleAddButtonWithType('MARKETING_OPT_OUT')}
                          disabled={templateButtons.some(b =>
                            b.type === 'QUICK_REPLY' &&
                            b.text &&
                            b.text.toLowerCase().includes('stop')
                          )}
                        >
                          <div className="wa-button-option-content">
                            <div className="wa-button-option-title">Marketing Opt-Out</div>
                            <small className="text-muted">Recommended for compliance</small>
                          </div>
                        </button>
                      </li>
                    )}

                    {/* Visit Website option - available for both Marketing and Utility */}
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => handleAddButtonWithType('URL')}
                        disabled={templateButtons.filter(b => b.type === 'URL').length >= 2}
                      >
                        <div className="wa-button-option-content">
                          <div className="wa-button-option-title">Visit Website</div>
                          <small className="text-muted">2 button maximum</small>
                        </div>
                      </button>
                    </li>

                    {/* Call Phone Number option - available for both Marketing and Utility */}
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => handleAddButtonWithType('PHONE_NUMBER')}
                        disabled={templateButtons.some(b => b.type === 'PHONE_NUMBER')}
                      >
                        <div className="wa-button-option-content">
                          <div className="wa-button-option-title">Call Phone Number</div>
                          <small className="text-muted">1 button maximum</small>
                        </div>
                      </button>
                    </li>

                    {/* Copy Offer Code option - only shown for Marketing category */}
                    {template.category === 'MARKETING' && (
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleAddButtonWithType('COPY_OFFER_CODE')}
                          disabled={templateButtons.some(b =>
                            b.type === 'QUICK_REPLY' &&
                            b.text &&
                            b.text.toLowerCase().includes('copy') &&
                            b.text.toLowerCase().includes('offer')
                          )}
                        >
                          <div className="wa-button-option-content">
                            <div className="wa-button-option-title">Copy Offer Code</div>
                            <small className="text-muted">1 button maximum</small>
                          </div>
                        </button>
                      </li>
                    )}

                    {/* Standard Quick Reply option - available for Utility category */}
                    {template.category === 'UTILITY' && (
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => handleAddButtonWithType('QUICK_REPLY')}
                        >
                          <div className="wa-button-option-content">
                            <div className="wa-button-option-title">Quick Reply</div>
                            <small className="text-muted">Simple interactive response</small>
                          </div>
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id="button-info-tooltip">
                      Buttons / CTA will be placed under the message template for your customer's easy access.
                    </Tooltip>
                  }
                >
                  <img
                    src={infoIcon}
                    alt="Info"
                    className="info-icon"
                  />
                </OverlayTrigger>
              </>
            )}
          </div>
          {template.category !== 'AUTHENTICATION' &&
            templateButtons.slice(0, 3).map((button, index) => (
              <ButtonTypeControl
                key={index}
                button={button}
                index={index}
                buttonTypes={buttonTypes}
                template={template}
                onButtonChange={onButtonChange}
                onRemoveButton={onRemoveButton}
              />
            ))}
        </div>
      </div>

      {template.category === 'AUTHENTICATION' && (
        <div className="section-header">
          <h6 className="button-title">Buttons</h6>
          <div className="subtitle">
            You can customize copy button for preview purposes
          </div>
          <Row>
            <Col sm={6}>
              <Form.Group className="wa-button-form-group">
                <Form.Label className="wa-button-form-label">Copy code (Preview only)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Copy code"
                  maxLength={25}
                  value={templateButtons.length > 0 && templateButtons[0].type === 'OTP' ? templateButtons[0].text || '' : ''}
                  onChange={(e) => {
                    if (templateButtons.length > 0 && templateButtons[0].type === 'OTP') {
                      onButtonChange(0, 'text', e.target.value);
                    } else {
                      // Create a new OTP button if it doesn't exist
                      onButtonChange(0, {
                        type: 'OTP',
                        text: e.target.value
                      });
                    }
                  }}
                  className="wa-button-text-input"
                />
                <div className="char-count text-end">
                  {templateButtons.length > 0 && templateButtons[0].type === 'OTP'
                    ? (templateButtons[0].text || '').length
                    : 0}/25 characters
                </div>
                <small className="text-muted mt-1">Note: WhatsApp doesn't support custom text for authentication copy buttons. This is only for preview.</small>
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}

      {/* Message Validity Period - Only for Utility and Authentication templates */}
      {(template.category === 'UTILITY' || template.category === 'AUTHENTICATION') && validityPeriod !== undefined && setValidityPeriod && (
        <div className="section-header">
          <MessageValidityPeriod
            validityPeriod={validityPeriod || 10}
            setValidityPeriod={setValidityPeriod}
            setCodeExp={template.category === 'AUTHENTICATION' ? setCodeExp : undefined}
          />
        </div>
      )}
    </div>
  );
};

export default TemplateForm;

// Add PropTypes for the component
TemplateForm.propTypes = {
  isEditing: PropTypes.bool,
  template: PropTypes.object.isRequired,
  templateHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  templateBody: PropTypes.string,
  templateFooter: PropTypes.string,
  templateButtons: PropTypes.array,
  bodyVariables: PropTypes.array,
  bodyVariableEntries: PropTypes.array,
  codeExp: PropTypes.number,
  bodyTextareaRef: PropTypes.object,
  languages: PropTypes.array,
  selectedLanguage: PropTypes.array,
  onLanguageChange: PropTypes.func,
  onTemplateChange: PropTypes.func,
  onBodyChange: PropTypes.func,
  setTemplateHeader: PropTypes.func,
  setTemplateFooter: PropTypes.func,
  setCodeExp: PropTypes.func,
  onAddVariable: PropTypes.func,
  onRemoveVariable: PropTypes.func,
  onVariableEntryChange: PropTypes.func,
  onButtonChange: PropTypes.func,
  onAddButton: PropTypes.func,
  onRemoveButton: PropTypes.func,
  onTextFormat: PropTypes.func,
  handleMediaUpload: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  uploadError: PropTypes.string,
  validityPeriod: PropTypes.number,
  setValidityPeriod: PropTypes.func,
  securityRecommendation: PropTypes.bool,
  setSecurityRecommendation: PropTypes.func,
  showExpiryTime: PropTypes.bool,
  setShowExpiryTime: PropTypes.func,
};
